import { z } from 'zod';
import { Email, IsoDateTime, Phone, Uuid } from './common.js';
import { PublicMember, PublicPreRegistration, PublicVisit } from './public-api.js';
/**
 * Public API v1, Phase 4 resources (ADR 0027, docs/modules/public-api-v1.md). Versioned like public-api.ts: never remove
 * or rename a field; add optional ones. Lists are cursor-paged. Owner: architect. Routes: S43 (sites, groups, members),
 * S44 (visits), S45 (pre-registrations). The OpenAPI document (S48) is generated from these schemas.
 */
/** Every list: `?limit=1..200&cursor=`; the cursor is opaque (base64url of the last sort key and id). */
export const ApiListQuery = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().max(200).optional(),
});
export const apiList = (item) => z.object({ items: z.array(item), nextCursor: z.string().nullable() });
// --- Sites and groups (read only, scope sites.read / groups.read) ---------------------------------------------------------
export const ApiSite = z.object({
    id: Uuid,
    name: z.string(),
    timeZone: z.string(),
    /** The calling client's external id for this site (its `system`), if linked. */
    externalId: z.string().nullable(),
    remote: z.boolean(),
});
export const ApiSiteList = apiList(ApiSite);
export const ApiGroup = z.object({
    id: Uuid,
    name: z.string(),
    kind: z.enum(['standard', 'repeat']),
    /** Sites where the group shows on kiosks. */
    siteIds: z.array(Uuid),
});
export const ApiGroupList = apiList(ApiGroup);
// --- Members (scope members.read / members.write) ----------------------------------------------------------------------------
export const ApiMember = PublicMember.extend({
    phone: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    hostMemberId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ApiMemberQuery = ApiListQuery.extend({
    groupId: Uuid.optional(),
    status: z.enum(['current', 'archived', 'all']).default('current'),
    email: z.string().max(254).optional(),
    updatedSince: IsoDateTime.optional(),
});
export const ApiMemberList = apiList(ApiMember);
/** POST /api/v1/members (Idempotency-Key). Only the client's allowed groups. Custom fields are not in v1. */
export const ApiMemberCreate = z.object({
    groupId: Uuid,
    fullName: z.string().trim().min(1).max(120),
    email: Email.nullable().default(null),
    phone: Phone.nullable().default(null),
    externalId: z.string().trim().min(1).max(100).nullable().default(null),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
    hostMemberId: Uuid.nullable().default(null),
});
/** PATCH /api/v1/members/:id: only the fields sent change. */
export const ApiMemberUpdate = z.object({
    fullName: z.string().trim().min(1).max(120).optional(),
    email: Email.nullable().optional(),
    phone: Phone.nullable().optional(),
    externalId: z.string().trim().min(1).max(100).nullable().optional(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    hostMemberId: Uuid.nullable().optional(),
});
/** POST /api/v1/members/:id/archive and /restore have no body. */
// --- Visits (scope visits.read) ---------------------------------------------------------------------------------------------
/** A query-string flag: 'true' / 'false' (or '1' / '0', or a real boolean). z.coerce.boolean() would turn the text 'false' into true. */
export const QueryBoolean = z.preprocess((v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : v), z.boolean());
export const ApiVisitQuery = ApiListQuery.extend({
    siteId: Uuid.optional(),
    groupId: Uuid.optional(),
    memberId: Uuid.optional(),
    /** Signed in at or after (UTC). Required: at most 93 days per request. */
    from: IsoDateTime,
    to: IsoDateTime.optional(),
    open: QueryBoolean.optional(),
});
export const ApiVisitList = apiList(PublicVisit);
// --- Pre-registrations (scope preregistrations.read / preregistrations.write) ----------------------------------------------
export const ApiPreRegistrationQuery = ApiListQuery.extend({
    siteId: Uuid.optional(),
    from: IsoDateTime,
    to: IsoDateTime.optional(),
    status: z.enum(['pending', 'approved', 'arrived', 'cancelled', 'all']).default('all'),
});
export const ApiPreRegistrationList = apiList(PublicPreRegistration);
/** POST /api/v1/preregistrations (Idempotency-Key). Source 'api'; waits for approval when the group says so. */
export const ApiPreRegistrationCreate = z.object({
    siteId: Uuid,
    groupId: Uuid,
    visitorName: z.string().trim().min(1).max(120),
    visitorEmail: Email.nullable().default(null),
    visitorPhone: Phone.nullable().default(null),
    visitorCompany: z.string().trim().max(120).nullable().default(null),
    hostMemberId: Uuid.nullable().default(null),
    expectedAt: IsoDateTime,
    expectedUntil: IsoDateTime.nullable().default(null),
    notes: z.string().max(1000).default(''),
    sendEmail: z.boolean().default(true),
    externalId: z.string().trim().min(1).max(100).nullable().default(null),
});
export const ApiPreRegistrationUpdate = z.object({
    expectedAt: IsoDateTime.optional(),
    expectedUntil: IsoDateTime.nullable().optional(),
    hostMemberId: Uuid.nullable().optional(),
    notes: z.string().max(1000).optional(),
    visitorPhone: Phone.nullable().optional(),
    visitorCompany: z.string().trim().max(120).nullable().optional(),
});
/** POST /api/v1/preregistrations/:id/cancel has no body. */
/** Every error body of the public API is the standard ApiError; 429 adds `Retry-After`. */
export const OPENAPI_PATH = '/api/v1/openapi.json';
//# sourceMappingURL=public-api-v1.js.map